import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import ApiService from 'helpers/apiService';

// Каталоги приходят с бэка (admin-api):
//   GET /notify/v1/event-rules/events    — 11 событий
//   GET /notify/v1/event-rules/channels  — 4 канала
type CatalogItem = { code: string; name: string };

// Запись матрицы. project_id может быть null (глобальный fallback).
type Rule = {
  id?: string;
  project_id?: string | null;
  event_code: string;
  channel_code: string;
  enabled: boolean;
  updated_at?: string;
  updated_by?: string | null;
};

// Группировка событий по разделам — для collapsible Cards (UX-вариант 2).
// Если событие не входит ни в одну группу — попадает в «Прочее».
const EVENT_GROUPS: { label: string; codes: string[] }[] = [
  { label: 'Безопасность', codes: ['registration', 'password_reset', 'account_blocked'] },
  { label: 'KYC', codes: ['kyc_approved', 'kyc_rejected', 'kyc_doc_requested'] },
  { label: 'Финансы', codes: ['deposit', 'withdrawal', 'account_freeze'] },
  { label: 'Крипто', codes: ['crypto_deposit', 'crypto_confirmed'] },
];

const groupHeaderStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  letterSpacing: '0.06em',
  color: '#212529',
};

// Ключ ячейки матрицы.
const cellKey = (event: string, channel: string) => `${event}|${channel}`;

const NotifyMatrixPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [events, setEvents] = useState<CatalogItem[]>([]);
  const [channels, setChannels] = useState<CatalogItem[]>([]);

  // Сохранённое состояние с бэка (effective view: project override > NULL global).
  // Map: "event|channel" → enabled.
  const [saved, setSaved] = useState<Map<string, boolean>>(new Map());
  // Локальные правки (dirty). Map того же формата.
  const [edits, setEdits] = useState<Map<string, boolean>>(new Map());

  // Свёрнутые группы (по умолчанию все развёрнуты).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [evRes, chRes, rulesRes] = await Promise.all([
        ApiService.getEventCatalog<CatalogItem[]>(),
        ApiService.getChannelCatalog<CatalogItem[]>(),
        ApiService.getEventRules<Rule[]>(),
      ]);
      setEvents(Array.isArray(evRes) ? evRes : []);
      setChannels(Array.isArray(chRes) ? chRes : []);
      const m = new Map<string, boolean>();
      (Array.isArray(rulesRes) ? rulesRes : []).forEach(r => {
        m.set(cellKey(r.event_code, r.channel_code), Boolean(r.enabled));
      });
      setSaved(m);
      setEdits(new Map()); // dirty сброс
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message || 'Не удалось загрузить матрицу';
      if (status === 503 || /sender gRPC not configured/i.test(msg)) {
        setError(
          'Admin-api не подключён к sender (env SENDER_SERVICE_GRPC_ADDR пуст). Матрица не может загрузиться без sender — задайте env в admin-api и перезапустите.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Эффективное состояние ячейки: edits если есть, иначе saved, иначе true (fail-open default).
  const cellState = useCallback(
    (event: string, channel: string): boolean => {
      const key = cellKey(event, channel);
      if (edits.has(key)) return edits.get(key) as boolean;
      if (saved.has(key)) return saved.get(key) as boolean;
      return true;
    },
    [edits, saved]
  );

  const isDirty = useCallback(
    (event: string, channel: string): boolean => {
      const key = cellKey(event, channel);
      if (!edits.has(key)) return false;
      const cur = edits.get(key);
      const sv = saved.has(key) ? saved.get(key) : true;
      return cur !== sv;
    },
    [edits, saved]
  );

  const toggle = (event: string, channel: string) => {
    const key = cellKey(event, channel);
    const cur = cellState(event, channel);
    setEdits(prev => {
      const next = new Map(prev);
      next.set(key, !cur);
      return next;
    });
  };

  // Счётчики для header.
  const counts = useMemo(() => {
    let on = 0;
    let off = 0;
    let dirty = 0;
    events.forEach(ev => {
      channels.forEach(ch => {
        const enabled = cellState(ev.code, ch.code);
        if (enabled) on++; else off++;
        if (isDirty(ev.code, ch.code)) dirty++;
      });
    });
    return { on, off, dirty, total: events.length * channels.length };
  }, [events, channels, cellState, isDirty]);

  // Группировка событий по разделам + «Прочее» для тех, что не в EVENT_GROUPS.
  const groupedEvents = useMemo(() => {
    const known = new Set(EVENT_GROUPS.flatMap(g => g.codes));
    const groups = EVENT_GROUPS.map(g => ({
      label: g.label,
      items: g.codes.map(c => events.find(e => e.code === c)).filter((x): x is CatalogItem => Boolean(x)),
    })).filter(g => g.items.length > 0);
    const others = events.filter(e => !known.has(e.code));
    if (others.length > 0) groups.push({ label: 'Прочее', items: others });
    return groups;
  }, [events]);

  const toggleGroup = (label: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const handleSave = async () => {
    if (counts.dirty === 0) return;
    const rules: Array<{ event_code: string; channel_code: string; enabled: boolean }> = [];
    edits.forEach((enabled, key) => {
      const [event, channel] = key.split('|');
      // Отправляем только реально изменённые строки (минимум payload + audit).
      const sv = saved.has(key) ? saved.get(key) : true;
      if (enabled !== sv) {
        rules.push({ event_code: event, channel_code: channel, enabled });
      }
    });
    if (rules.length === 0) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await ApiService.upsertEventRules(rules);
      // Bug 8 (2026-06-01): post-save верификация. Бэк отдаёт effective view
      // (project override > NULL global). После save проверяем, что то, что
      // мы отправляли, действительно отразилось в БД. Если расходится —
      // показываем конкретные ячейки. Это диагностика на случай, когда:
      //   - X-Project-ID не доходит до бэка (нет в localStorage / interceptor)
      //   - admin-api не подключён к sender (senderConfigSvc == nil → 500)
      //   - sender gRPC падает (миграция не применена / stale code)
      // Раньше любые такие проблемы давали ложный "Сохранено: N" без проверки.
      const [evRes, chRes, rulesRes] = await Promise.all([
        ApiService.getEventCatalog<CatalogItem[]>(),
        ApiService.getChannelCatalog<CatalogItem[]>(),
        ApiService.getEventRules<Rule[]>(),
      ]);
      setEvents(Array.isArray(evRes) ? evRes : []);
      setChannels(Array.isArray(chRes) ? chRes : []);
      const nextSaved = new Map<string, boolean>();
      (Array.isArray(rulesRes) ? rulesRes : []).forEach(r => {
        nextSaved.set(cellKey(r.event_code, r.channel_code), Boolean(r.enabled));
      });
      const mismatches: string[] = [];
      rules.forEach(r => {
        const k = cellKey(r.event_code, r.channel_code);
        const actual = nextSaved.has(k) ? nextSaved.get(k) : true;
        if (actual !== r.enabled) {
          mismatches.push(`${r.event_code}/${r.channel_code}`);
        }
      });
      setSaved(nextSaved);
      setEdits(new Map());
      if (mismatches.length > 0) {
        setError(
          `Отправили ${rules.length}, но в БД не совпали: ${mismatches.slice(0, 5).join(', ')}${
            mismatches.length > 5 ? ` …+${mismatches.length - 5}` : ''
          }. Проверьте X-Project-ID в DevTools (localStorage.currentProjectId) и логи admin-api.`
        );
      } else {
        setSuccessMsg(`Сохранено: ${rules.length} ✓ верифицировано`);
        setTimeout(() => setSuccessMsg(null), 3000);
      }
    } catch (e: any) {
      // 503 от admin-api → senderConfigSvc == nil (env SENDER_SERVICE_GRPC_ADDR не задан
      // или sender недоступен). Воспроизводится в основном на ЛОКАЛЬНЫХ admin-api без
      // правильного env. На test admin-api всё работает (live verification 2026-06-01).
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message || 'Не удалось сохранить';
      if (status === 503 || /sender gRPC not configured/i.test(msg)) {
        setError(
          'Admin-api не подключён к sender (env SENDER_SERVICE_GRPC_ADDR пуст или sender недоступен). На локальном admin-api — задайте env и перезапустите. На задеплоенном test — обратитесь в DEVOPS.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEdits(new Map());
    setSuccessMsg(null);
  };

  // Group counts для бейджа в header карточки.
  const groupCounts = (codes: CatalogItem[]) => {
    let on = 0;
    let off = 0;
    codes.forEach(ev => {
      channels.forEach(ch => {
        if (cellState(ev.code, ch.code)) on++; else off++;
      });
    });
    return { on, off };
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Матрица каналов</h5>
              <p className="text-muted small mb-0">
                Управление каналами доставки нотификаций по бизнес-событиям. Изменения применяются ≤5 мин (TTL кеша sender worker).
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {!loading && (
                <>
                  <Badge bg="success" pill style={{ fontSize: '0.75rem' }}>● {counts.on} вкл</Badge>
                  <Badge bg="secondary" pill style={{ fontSize: '0.75rem' }}>○ {counts.off} выкл</Badge>
                  {counts.dirty > 0 && (
                    <Badge bg="warning" text="dark" pill style={{ fontSize: '0.75rem' }}>◆ {counts.dirty} несохр.</Badge>
                  )}
                </>
              )}
              <Button variant="outline-secondary" size="sm" onClick={fetchAll} disabled={loading || saving} title="Обновить">
                {loading ? <Spinner animation="border" size="sm" /> : '↻'}
              </Button>
              {counts.dirty > 0 && (
                <Button variant="outline-secondary" size="sm" onClick={handleReset} disabled={saving}>
                  Отменить
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={saving || counts.dirty === 0}
              >
                {saving ? <><Spinner animation="border" size="sm" className="me-1" />Сохранение…</> : `Сохранить${counts.dirty > 0 ? ` (${counts.dirty})` : ''}`}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="py-2 small" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert variant="success" className="py-2 small" dismissible onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {loading ? (
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" size="sm" className="me-2" />
            Загрузка матрицы…
          </Card.Body>
        </Card>
      ) : events.length === 0 || channels.length === 0 ? (
        <Card>
          <Card.Body className="text-muted small">
            Каталоги событий или каналов недоступны.
          </Card.Body>
        </Card>
      ) : (
        groupedEvents.map(group => {
          const isCollapsed = collapsed.has(group.label);
          const { on, off } = groupCounts(group.items);
          return (
            <Card key={group.label} className="mb-3">
              <Card.Header
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleGroup(group.label)}
              >
                <span className="fw-semibold text-uppercase" style={groupHeaderStyle}>
                  {group.label}
                  <Badge bg="light" text="dark" className="ms-2 border" style={{ fontWeight: 400 }}>
                    {group.items.length} событий
                  </Badge>
                  <Badge bg="success" className="ms-1" style={{ fontWeight: 400 }}>{on} вкл</Badge>
                  {off > 0 && <Badge bg="secondary" className="ms-1" style={{ fontWeight: 400 }}>{off} выкл</Badge>}
                </span>
                <span className="text-muted small">{isCollapsed ? '▶' : '▼'}</span>
              </Card.Header>
              {!isCollapsed && (
                <Card.Body className="p-0">
                  <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Событие</th>
                        {channels.map(ch => (
                          <th key={ch.code} className="text-center" style={{ width: 110 }}>
                            {ch.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map(ev => (
                        <tr key={ev.code}>
                          <td>
                            <div className="fw-semibold">{ev.name}</div>
                            <div className="text-muted small font-monospace" style={{ fontSize: '0.7rem' }}>{ev.code}</div>
                          </td>
                          {channels.map(ch => {
                            const enabled = cellState(ev.code, ch.code);
                            const dirty = isDirty(ev.code, ch.code);
                            return (
                              <td
                                key={ch.code}
                                className="text-center"
                                style={dirty ? { background: '#fff3cd' } : undefined}
                              >
                                <Form.Check
                                  type="checkbox"
                                  checked={enabled}
                                  onChange={() => toggle(ev.code, ch.code)}
                                  aria-label={`${ev.name} — ${ch.name}: ${enabled ? 'вкл' : 'выкл'}`}
                                  style={{ display: 'inline-block' }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              )}
            </Card>
          );
        })
      )}
    </>
  );
};

export default NotifyMatrixPage;
